import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";
import { CASINO_BAR, CASINO_CHIPS_SIGN, CASINO_LOUNGE, CASINO_SLOTS, CASINO_TABLES, CASINO_WHEEL } from "@townsy/world";
import { PartBatch, applyPart, cylinder, sphere } from "../../../scene/core/meshKit";
import { litTexture, litVertex, paintedTexture, unlitTexture, unlitVertex, type Bag } from "./kit";
import {
  FELT_TEX,
  SLOT_COLORS,
  TOPPER_TEX,
  paintBetLayout,
  paintCarpet,
  paintChipsSign,
  paintFelt,
  paintMedallion,
  paintPlanks,
  paintSign,
  paintSlotMarquee,
  paintToppers,
  uvOf,
} from "./paint";
import { QuadBatch, bottle, figure } from "./parts";

/**
 * The casino floor's fixed 3D (ADR-0016), on top of the generic room (floor, walls, door, exit mat, the lounge's sofas
 * and plants): carpets, wall panels and lights, the three blackjack tables, eight slot machines with their marquee,
 * the money wheel's stage, frame and betting counter, the bar, the dealers, and the play-chips sign.
 * These are the room's "venue_" fixtures (the generic builder skips them); each drawn shape encloses its collider.
 */
export const C = {
  woodDark: "#4A2E1F",
  wood: "#6B4429",
  gold: "#C9A54A",
  leather: "#3A1F17",
  black: "#1E1F24",
  marble: "#E9E2D3",
  wall: "#2B5563",
  cream: "#F3E6C8",
  purple: "#2B1B3D",
  navy: "#1E2A4A",
  steel: "#9BA3AE",
  warm: "#FFD27A",
} as const;

/** Heights shared with the dynamic views. */
export const FELT_Y = 0.93;
export const COUNTER_TOP_Y = 1.045;
export const REEL_Y = 1.45;
/** Felt radius (fraction of the table radius) where each seat's chips go, and where its cards lie. */
export const CHIP_RING = 0.84;
export const CARD_RING = 0.68;

/** The medallion rug in the middle of the floor, on top of the carpet (the generic centre rug is not drawn in venues). */
const MEDALLION = { x: 0, z: -2.4, w: 5.4, d: 3.9, y: 0.03 } as const;

const DEALER = { vest: "#7A1F2B", shirt: "#F4F1EA", skin: "#C98E6B", hair: "#2A1C14", tie: "#1E1F24" };
const DEALERS = [DEALER, { ...DEALER, skin: "#8D5A3B", hair: "#141010" }, { ...DEALER, skin: "#EDC2A0", hair: "#7A4B26" }];

export interface StaticCasino {
  meshes: Mesh[];
  casters: Mesh[];
  /** Betting layout cells on the wheel counter (x range in metres, centre z). */
  layoutCells: Array<{ symbol: string; x0: number; x1: number; z: number }>;
}

export function buildStatic(scene: Scene, bag: Bag): StaticCasino {
  const lit = bag.add(litVertex(scene, "casinoLit"));
  const glowMat = bag.add(unlitVertex(scene, "casinoGlow"));
  const b = new PartBatch();
  const g = new PartBatch();
  const meshes: Mesh[] = [];

  walls(b, g);
  borders(b);
  tables(scene, b, g);
  slots(scene, b, g);
  wheel(scene, b, g);
  bar(scene, b, g);
  lounge(scene, b, g);
  chipsSign(b);

  const solid = b.build("casinoStatic", scene);
  const casters: Mesh[] = [];
  if (solid) {
    solid.material = lit;
    solid.receiveShadows = true;
    solid.freezeWorldMatrix();
    meshes.push(bag.add(solid));
    casters.push(solid);
  }
  const glowing = g.build("casinoGlowStatic", scene);
  if (glowing) {
    glowing.material = glowMat;
    glowing.freezeWorldMatrix();
    meshes.push(bag.add(glowing));
  }

  // ---- textured surfaces ----
  const floorMats = {
    plum: litTexture(scene, "casinoCarpetPlum", paintedTexture(bag, scene, "casinoCarpetPlumTex", 256, 256, (c) => paintCarpet(c, "#3B2440", "#6E4A73", "#B8893A"), { wrap: true }), 0.1),
    red: litTexture(scene, "casinoCarpetRed", paintedTexture(bag, scene, "casinoCarpetRedTex", 256, 256, (c) => paintCarpet(c, "#6E1E2A", "#B8893A", "#D9B25A"), { wrap: true }), 0.12),
    blue: litTexture(scene, "casinoCarpetBlue", paintedTexture(bag, scene, "casinoCarpetBlueTex", 256, 256, (c) => paintCarpet(c, "#1F3463", "#B8893A", "#D9B25A"), { wrap: true }), 0.12),
    wood: litTexture(scene, "casinoPlanks", paintedTexture(bag, scene, "casinoPlanksTex", 256, 256, paintPlanks, { wrap: true }), 0.08),
  };
  for (const m of Object.values(floorMats)) bag.add(m);
  const floorY = 0.012;
  const rugY = 0.022;
  const plum = new QuadBatch().floor(-19.98, -12.6, 7.2, 14.98, floorY, 2.4).floor(7.2, -12.6, 19.98, 8.2, floorY, 2.4);
  const red = new QuadBatch().floor(-19.6, -9.4, -4.2, 6.8, rugY, 2.4).floor(-5.6, 6.8, 5.6, 14.95, rugY, 2.4).floor(-19.6, 8, -12.4, 14.6, rugY, 2.4);
  const blue = new QuadBatch().floor(5.4, -2.6, 19.6, 3.6, rugY, 2.4);
  const wood = new QuadBatch().floor(7.2, 8.2, 19.98, 14.98, floorY, 2.2);
  for (const [batch, mat, name] of [
    [plum, floorMats.plum, "casinoCarpetPlumMesh"],
    [red, floorMats.red, "casinoCarpetRedMesh"],
    [blue, floorMats.blue, "casinoCarpetBlueMesh"],
    [wood, floorMats.wood, "casinoPlanksMesh"],
  ] as const) {
    const mesh = bag.add(batch.build(name, scene));
    mesh.material = mat;
    mesh.receiveShadows = true;
    mesh.freezeWorldMatrix();
    meshes.push(mesh);
  }

  // A medallion rug in the middle of the room.
  const medTex = paintedTexture(bag, scene, "casinoMedallionTex", 512, 368, (c) => paintMedallion(c, 512, 368));
  const medMat = bag.add(litTexture(scene, "casinoMedallion", medTex, 0.14));
  const M = MEDALLION;
  const medMesh = bag.add(new QuadBatch().flat(M.x, M.y, M.z, M.w, M.d, 0, 1, { u0: 0, v0: 0, u1: 1, v1: 1 }).build("casinoMedallionMesh", scene));
  medMesh.material = medMat;
  medMesh.receiveShadows = true;
  medMesh.freezeWorldMatrix();
  meshes.push(medMesh);

  // Blackjack felts: one texture, three half discs.
  const t0 = CASINO_TABLES[0]!;
  const feltTex = paintedTexture(bag, scene, "casinoFeltTex", FELT_TEX.width, FELT_TEX.height, (c) => paintFelt(c, t0.seatDirs, CHIP_RING));
  const feltMat = bag.add(litTexture(scene, "casinoFelt", feltTex, 0.22));
  const felts = new QuadBatch();
  for (const t of CASINO_TABLES) felts.halfDisc(t.pivot.x, t.pivot.z, t.radius, FELT_Y, 48);
  const feltMesh = bag.add(felts.build("casinoFelts", scene));
  feltMesh.material = feltMat;
  feltMesh.receiveShadows = true;
  feltMesh.freezeWorldMatrix();
  meshes.push(feltMesh);

  // Signs (unlit so they read in any light). Atlas rows: wheel title 0..128, bar 128..288, chips sign 288..608,
  // betting layout 608..736.
  let cellsFrac: Array<{ symbol: string; x0: number; x1: number }> = [];
  const signTex = paintedTexture(bag, scene, "casinoSignsTex", 1024, 1024, (c) => {
    c.fillStyle = "#000";
    c.fillRect(0, 0, 1024, 1024);
    c.save();
    paintSign(c, 1024, 128, { title: "MONEY WHEEL", sub: "PICK 1 · 2 · 5 · 10 · 20 · STAR", bg: "#2B1B3D", ink: "#FFE27A", edge: "#C9A54A", titleSize: 56, subSize: 28 });
    c.translate(0, 128);
    paintSign(c, 512, 160, { title: "BAR", sub: "FREE MOCKTAILS", bg: "#141A2E", ink: "#FF8FB1", edge: "#7FE0FF", titleSize: 82, subSize: 26 });
    c.translate(0, 160);
    paintChipsSign(c, 512, 320);
    c.translate(0, 320);
    cellsFrac = paintBetLayout(c, 1024, 128);
    c.restore();
  });
  const signMat = bag.add(unlitTexture(scene, "casinoSigns", signTex));
  const W = CASINO_WHEEL;
  const signs = new QuadBatch();
  signs.upright(0, 0.5, W.stage.minZ - 0.03, 3.9, 0.49, Math.PI, uvOf(0, 0, 1024, 128, 1024, 1024));
  signs.upright(CASINO_BAR.bartender.x, 2.72, 14.92, 2.6, 0.81, Math.PI, uvOf(0, 128, 512, 160, 1024, 1024));
  const cs = CASINO_CHIPS_SIGN;
  signs.upright(cs.position.x, 1.5, cs.position.z - 0.22, 1.25, 0.78, Math.PI, uvOf(0, 288, 512, 320, 1024, 1024));
  // The betting layout lies on the wheel counter, readable from the players' side.
  const cz = (W.counter.minZ + W.counter.maxZ) / 2;
  const layoutW = W.counter.maxX - W.counter.minX - 0.12;
  signs.flat(0, COUNTER_TOP_Y, cz, layoutW, 0.6, 0, 1, uvOf(0, 608, 1024, 128, 1024, 1024));
  const signMesh = bag.add(signs.build("casinoSignsMesh", scene));
  signMesh.material = signMat;
  signMesh.freezeWorldMatrix();
  meshes.push(signMesh);

  // The marquee over the slot row (its own long texture).
  const marqueeTex = paintedTexture(bag, scene, "casinoMarqueeTex", 2048, 112, (c) => paintSlotMarquee(c, 2048, 112));
  const marqueeMat = bag.add(unlitTexture(scene, "casinoMarquee", marqueeTex));
  const first = CASINO_SLOTS[0]!.position;
  const last = CASINO_SLOTS[CASINO_SLOTS.length - 1]!.position;
  const marqueeW = last.x - first.x + 1.3;
  const marquee = new QuadBatch().upright((first.x + last.x) / 2, 2.95, first.z + 0.18, marqueeW, marqueeW * (112 / 2048), Math.PI, uvOf(0, 0, 2048, 112, 2048, 112));
  const marqueeMesh = bag.add(marquee.build("casinoMarqueeMesh", scene));
  marqueeMesh.material = marqueeMat;
  marqueeMesh.freezeWorldMatrix();
  meshes.push(marqueeMesh);

  // Slot toppers (their own atlas: one row per machine).
  const topTex = paintedTexture(bag, scene, "casinoToppersTex", TOPPER_TEX.width, TOPPER_TEX.height, paintToppers);
  const topMat = bag.add(unlitTexture(scene, "casinoToppers", topTex));
  const tops = new QuadBatch();
  CASINO_SLOTS.forEach((s, i) => {
    tops.upright(s.position.x, 2.18, s.position.z - 0.245, 0.96, 0.24, Math.PI, uvOf(0, i * TOPPER_TEX.rowH, TOPPER_TEX.width, TOPPER_TEX.rowH, TOPPER_TEX.width, TOPPER_TEX.height));
  });
  const topMesh = bag.add(tops.build("casinoToppersMesh", scene));
  topMesh.material = topMat;
  topMesh.freezeWorldMatrix();
  meshes.push(topMesh);

  const layoutCells = cellsFrac.map((c) => ({ symbol: c.symbol, x0: -layoutW / 2 + c.x0 * layoutW, x1: -layoutW / 2 + c.x1 * layoutW, z: cz }));
  return { meshes, casters, layoutCells };
}

// ---------------------------------------------------------------------------------------------------------------------

function walls(b: PartBatch, g: PartBatch): void {
  const H0 = 1.1;
  const H1 = 3.26;
  const h = H1 - H0;
  const y = (H0 + H1) / 2;
  // Upper wall panels (in front of the plain walls), with gold pinstripes.
  b.box(40, h, 0.02, C.wall, 0, y, 14.965);
  b.box(0.02, h, 30, C.wall, 19.965, y, 0);
  b.box(0.02, h, 30, C.wall, -19.965, y, 0);
  for (const yy of [H0 + 0.02, H1 - 0.03]) {
    b.box(40, 0.03, 0.02, C.gold, 0, yy, 14.945);
    b.box(0.02, 0.03, 30, C.gold, 19.945, yy, 0);
    b.box(0.02, 0.03, 30, C.gold, -19.945, yy, 0);
  }
  // Pilasters with warm sconces on the side walls.
  for (const z of [-12.5, -7.5, -2.5, 2.5, 7.5]) {
    for (const side of [-1, 1]) {
      const x = side * 19.9;
      b.boxBase(0.14, 3.4, 0.42, C.cream, x, 0, z);
      b.boxBase(0.18, 0.08, 0.48, C.gold, x, 3.2, z);
      b.boxBase(0.18, 0.08, 0.48, C.gold, x, 1.0, z);
      g.box(0.1, 0.22, 0.16, C.warm, x - side * 0.12, 2.45, z);
    }
  }
  // Sconces on the north wall between the features.
  for (const x of [-18.5, -4.6, 4.6, 7.5]) g.box(0.2, 0.22, 0.08, C.warm, x, 2.45, 14.9);
}

function borders(b: PartBatch): void {
  const edge = (minX: number, minZ: number, maxX: number, maxZ: number) => {
    const t = 0.07;
    const y = 0.008;
    const h = 0.03;
    b.boxBase(maxX - minX, h, t, C.gold, (minX + maxX) / 2, y, minZ);
    b.boxBase(maxX - minX, h, t, C.gold, (minX + maxX) / 2, y, maxZ);
    b.boxBase(t, h, maxZ - minZ, C.gold, minX, y, (minZ + maxZ) / 2);
    b.boxBase(t, h, maxZ - minZ, C.gold, maxX, y, (minZ + maxZ) / 2);
  };
  edge(-19.6, -9.4, -4.2, 6.8);
  edge(5.4, -2.6, 19.6, 3.6);
  edge(-19.6, 8, -12.4, 14.6);
  edge(-5.6, 6.8, 5.6, 14.95);
  edge(MEDALLION.x - MEDALLION.w / 2, MEDALLION.z - MEDALLION.d / 2, MEDALLION.x + MEDALLION.w / 2, MEDALLION.z + MEDALLION.d / 2);
}

function tables(scene: Scene, b: PartBatch, g: PartBatch): void {
  for (const t of CASINO_TABLES) {
    const { x: px, z: pz } = t.pivot;
    const R = t.radius;
    // Body: a half cylinder (the south half), enclosing the inscribed colliders.
    const body = CreateCylinder("bjBody", { height: 0.86, diameter: (R + 0.02) * 2, tessellation: 48, arc: 0.5, enclose: true }, scene);
    applyPart(body, C.woodDark, { pos: { x: px, y: 0.43, z: pz } });
    b.add(body);
    // Dealer side panel and a brass kick plate.
    b.boxBase(R * 2 + 0.08, 0.9, 0.08, C.wood, px, 0, pz + 0.02);
    b.boxBase(R * 2 + 0.1, 0.06, 0.1, C.gold, px, 0.88, pz + 0.02);
    const kick = CreateCylinder("bjKick", { height: 0.12, diameter: (R + 0.05) * 2, tessellation: 48, arc: 0.5, enclose: true }, scene);
    applyPart(kick, C.black, { pos: { x: px, y: 0.06, z: pz } });
    b.add(kick);
    // Padded armrest around the arc.
    const n = 28;
    for (let k = 0; k < n; k++) {
      const a = (Math.PI * (k + 0.5)) / n;
      const r = R + 0.06;
      const len = 2 * r * Math.sin(Math.PI / n / 2) * 1.12;
      b.box(len, 0.1, 0.17, C.leather, px + Math.cos(a) * r, 0.9, pz - Math.sin(a) * r, a + Math.PI / 2);
    }
    // Chip rack and card shoe on the dealer's side.
    b.boxBase(0.74, 0.05, 0.2, C.black, px, FELT_Y, pz - 0.16);
    ["#24242C", "#2E8B57", "#2F5FD0", "#C8323C", "#F1EDE4"].forEach((col, i) => b.boxBase(0.11, 0.035, 0.15, col, px - 0.28 + i * 0.14, FELT_Y + 0.05, pz - 0.16));
    b.boxBase(0.18, 0.13, 0.28, "#5A1E22", px + 0.62, FELT_Y, pz - 0.22);
    b.boxBase(0.14, 0.02, 0.2, C.cream, px + 0.62, FELT_Y + 0.13, pz - 0.23);
    // A dealer stands behind the table.
    figure(scene, b, t.dealer.x, t.dealer.z - 0.2, t.dealer.facing, DEALERS[t.index % DEALERS.length]!);
    // A warm pendant lamp high above the felt.
    const lx = px;
    const lz = pz - 0.75;
    b.box(0.02, 0.3, 0.02, C.black, lx, 3.3, lz);
    b.add(cylinder(scene, "bjShade", { height: 0.18, diameterTop: 0.18, diameterBottom: 0.5, tessellation: 16 }, "#1E5E46", { pos: { x: lx, y: 3.08, z: lz } }));
    g.add(cylinder(scene, "bjBulb", { height: 0.02, diameter: 0.45, tessellation: 16 }, C.warm, { pos: { x: lx, y: 2.985, z: lz } }));
  }
}

function slots(scene: Scene, b: PartBatch, g: PartBatch): void {
  CASINO_SLOTS.forEach((s, i) => {
    const { x, z } = s.position;
    const accent = SLOT_COLORS[i % SLOT_COLORS.length]!;
    // Base cabinet (encloses the collider), upper cabinet set back, accent side panels.
    b.boxBase(1.04, 1.05, 0.94, "#2A2733", x, 0, z);
    b.boxBase(1.0, 0.95, 0.7, "#2A2733", x, 1.05, z + 0.12);
    for (const side of [-1, 1]) b.boxBase(0.03, 2.0, 0.96, accent, x + side * 0.535, 0, z);
    b.boxBase(1.02, 0.12, 0.72, accent, x, 2.0, z + 0.12);
    // Screen bezel around the reels and the payline.
    b.box(0.86, 0.6, 0.03, "#121218", x, REEL_Y, z - 0.245);
    b.box(0.9, 0.04, 0.05, C.gold, x, REEL_Y + 0.32, z - 0.25);
    b.box(0.9, 0.04, 0.05, C.gold, x, REEL_Y - 0.32, z - 0.25);
    g.box(0.8, 0.014, 0.01, "#FF3B3B", x, REEL_Y, z - 0.272);
    // Button deck with three lit buttons, a coin tray, the lever on the player's right.
    b.box(1.0, 0.06, 0.24, "#3A3646", x, 1.08, z - 0.35);
    ["#FF5A5A", "#FFD24A", "#5AD1FF"].forEach((col, k) => g.box(0.12, 0.03, 0.08, col, x - 0.25 + k * 0.25, 1.12, z - 0.37));
    b.box(0.6, 0.05, 0.12, C.steel, x, 0.55, z - 0.49);
    b.add(cylinder(scene, "slotLever", { height: 0.42, diameter: 0.04, tessellation: 8 }, C.steel, { pos: { x: x + 0.58, y: 1.42, z: z - 0.05 } }));
    b.add(sphere(scene, "slotKnob", 0.11, 8, "#E3262E", { pos: { x: x + 0.58, y: 1.66, z: z - 0.05 } }));
    // Topper frame (the name is a textured quad in front of it).
    b.boxBase(1.02, 0.34, 0.12, "#1A1820", x, 2.0, z - 0.17);
    // Candle lamp post (the lamp itself blinks: dynamic).
    b.add(cylinder(scene, "slotCandle", { height: 0.38, diameter: 0.05, tessellation: 8 }, C.steel, { pos: { x, y: 2.31, z: z + 0.25 } }));
  });
  // Marquee posts at both ends of the row.
  const first = CASINO_SLOTS[0]!.position;
  const last = CASINO_SLOTS[CASINO_SLOTS.length - 1]!.position;
  for (const x of [first.x - 0.72, last.x + 0.72]) b.boxBase(0.1, 3.2, 0.1, C.gold, x, 0, first.z + 0.22);
  b.box(last.x - first.x + 1.5, 0.08, 0.14, C.gold, (first.x + last.x) / 2, 3.24, first.z + 0.22);
  b.box(last.x - first.x + 1.5, 0.66, 0.06, "#1A1430", (first.x + last.x) / 2, 2.95, first.z + 0.24);
}

function wheel(scene: Scene, b: PartBatch, g: PartBatch): void {
  const W = CASINO_WHEEL;
  const sx = (W.stage.minX + W.stage.maxX) / 2;
  const sz = (W.stage.minZ + W.stage.maxZ) / 2;
  // Stage (encloses its collider) with a gold lip and a row of bulbs.
  b.boxBase(W.stage.maxX - W.stage.minX + 0.04, 1.0, W.stage.maxZ - W.stage.minZ + 0.04, C.purple, sx, 0, sz);
  b.box(W.stage.maxX - W.stage.minX + 0.1, 0.05, W.stage.maxZ - W.stage.minZ + 0.1, C.gold, sx, 1.0, sz);
  for (let k = 0; k < 11; k++) g.add(sphere(scene, "stageBulb", 0.07, 6, C.warm, { pos: { x: W.stage.minX + 0.25 + k * 0.39, y: 0.86, z: W.stage.minZ - 0.03 } }));
  // Backdrop panel against the wall, framed in gold.
  b.box(5.2, 2.4, 0.06, C.navy, 0, 2.2, 14.92);
  b.box(5.3, 0.06, 0.08, C.gold, 0, 3.4, 14.9);
  for (const x of [-2.62, 2.62]) b.box(0.06, 2.4, 0.08, C.gold, x, 2.2, 14.9);
  // Two posts and an axle hold the wheel; a gold frame ring around it; the pointer at the top.
  const az = W.position.z;
  for (const x of [-0.75, 0.75]) b.boxBase(0.14, W.hubY - 1.0 + 0.1, 0.14, C.gold, x, 1.0, az + 0.22);
  b.box(1.64, 0.12, 0.12, C.gold, 0, W.hubY, az + 0.22);
  const frame = CreateTorus("wheelFrame", { diameter: (W.radius + 0.1) * 2, thickness: 0.09, tessellation: 64 }, scene);
  applyPart(frame, C.gold, { pos: { x: 0, y: W.hubY, z: az - 0.02 }, rot: { x: Math.PI / 2 } });
  b.add(frame);
  // Pointer: a small red arrow over the top of the wheel, pointing down.
  const top = W.hubY + W.radius + 0.13;
  const tri = CreateCylinder("wheelPointer", { height: 0.08, diameter: 0.34, tessellation: 3 }, scene);
  applyPart(tri, "#E3262E", { pos: { x: 0, y: top, z: az - 0.12 }, rot: { x: Math.PI / 2, y: 0, z: Math.PI / 2 } });
  b.add(tri);
  b.add(sphere(scene, "pointerCap", 0.1, 8, C.gold, { pos: { x: 0, y: top + 0.1, z: az - 0.12 } }));
  // Betting counter (encloses its collider), a gold-edged top and a lit strip on the players' side.
  const c = W.counter;
  const cx = (c.minX + c.maxX) / 2;
  const cz = (c.minZ + c.maxZ) / 2;
  b.boxBase(c.maxX - c.minX + 0.04, 1.0, c.maxZ - c.minZ + 0.04, C.purple, cx, 0, cz);
  b.box(c.maxX - c.minX + 0.16, 0.04, c.maxZ - c.minZ + 0.16, C.gold, cx, 1.02, cz);
  g.box(c.maxX - c.minX - 0.2, 0.05, 0.02, C.warm, cx, 0.82, c.minZ - 0.03);
}

function bar(scene: Scene, b: PartBatch, g: PartBatch): void {
  const B = CASINO_BAR;
  const box = (a: { minX: number; minZ: number; maxX: number; maxZ: number }, h: number, col: string, pad = 0.02) =>
    b.boxBase(a.maxX - a.minX + pad * 2, h, a.maxZ - a.minZ + pad * 2, col, (a.minX + a.maxX) / 2, 0, (a.minZ + a.maxZ) / 2);
  // Counter and its return: wood, a marble top, a warm strip under the lip, a brass foot rail.
  for (const part of [B.counter, B.counterReturn]) {
    box(part, 1.05, C.woodDark);
    b.box(part.maxX - part.minX + 0.12, 0.06, part.maxZ - part.minZ + 0.14, C.marble, (part.minX + part.maxX) / 2, 1.08, (part.minZ + part.maxZ) / 2);
  }
  const front = B.counter.minZ;
  g.box(B.counter.maxX - B.counter.minX - 0.2, 0.04, 0.02, "#FFC46B", (B.counter.minX + B.counter.maxX) / 2, 0.98, front - 0.04);
  b.box(B.counter.maxX - B.counter.minX - 0.3, 0.05, 0.05, C.gold, (B.counter.minX + B.counter.maxX) / 2, 0.22, front - 0.16);
  // Glasses on the counter.
  for (let k = 0; k < 6; k++) b.add(cylinder(scene, "barGlass", { height: 0.14, diameterTop: 0.09, diameterBottom: 0.07, tessellation: 8 }, "#BFDDEB", { pos: { x: 10.6 + k * 1.6, y: 1.18, z: (B.counter.minZ + B.counter.maxZ) / 2 + 0.15 } }));
  // Back bar: cabinet, mirror, two glass shelves full of bottles, a warm light strip.
  const bb = B.backBar;
  box(bb, 1.0, C.wood);
  const bx = (bb.minX + bb.maxX) / 2;
  const bw = bb.maxX - bb.minX;
  b.box(bw + 0.06, 0.04, bb.maxZ - bb.minZ + 0.08, C.marble, bx, 1.02, (bb.minZ + bb.maxZ) / 2);
  b.box(bw, 1.05, 0.02, "#8FA9BC", bx, 1.62, 14.935);
  for (const y of [1.36, 1.76]) {
    b.box(bw, 0.03, 0.26, "#CFE3EC", bx, y, 14.84);
    g.box(bw, 0.015, 0.02, C.warm, bx, y - 0.03, 14.72);
  }
  const palette = ["#7A2E1F", "#2E6B3A", "#B8892A", "#3F5FA8", "#8C2F5E", "#D9C27A", "#4B8C8C", "#5A3A22"];
  let k = 0;
  for (const y of [1.04, 1.38, 1.78]) {
    for (let x = bb.minX + 0.3; x < bb.maxX - 0.2; x += 0.27) {
      if (y === 1.04 && Math.abs(x - B.bartender.x) < 1.2) continue;
      bottle(scene, b, x, y, 14.82 - (k % 2) * 0.06, palette[k % palette.length]!, 0.26 + (k % 3) * 0.04);
      k += 1;
    }
  }
  // Stools along the counter (no colliders: people stand among them).
  for (const s of B.stools) {
    const z = B.counter.minZ - 0.38;
    b.add(cylinder(scene, "stoolBase", { height: 0.03, diameter: 0.36, tessellation: 12 }, C.black, { pos: { x: s.x, y: 0.015, z } }));
    b.add(cylinder(scene, "stoolPole", { height: 0.7, diameter: 0.06, tessellation: 8 }, C.steel, { pos: { x: s.x, y: 0.38, z } }));
    b.add(cylinder(scene, "stoolSeat", { height: 0.09, diameter: 0.4, tessellation: 14 }, "#9E2430", { pos: { x: s.x, y: 0.76, z } }));
    const ring = CreateTorus("stoolRing", { diameter: 0.3, thickness: 0.025, tessellation: 16 }, scene);
    applyPart(ring, C.steel, { pos: { x: s.x, y: 0.3, z } });
    b.add(ring);
  }
  // The bartender is a real AI resident actor (the casino game adds it when AI residents are on), not decor.
}

function lounge(scene: Scene, b: PartBatch, g: PartBatch): void {
  // Floor lamps beside the sofas (the sofas and coffee table are the room's own furniture).
  const L = CASINO_LOUNGE;
  for (const [x, z] of [
    [L.sofaNorth.minX - 0.5, L.sofaNorth.minZ + 0.4],
    [L.sofaNorth.maxX + 0.5, L.sofaNorth.minZ + 0.4],
  ] as const) {
    b.add(cylinder(scene, "lampPole", { height: 1.6, diameter: 0.04, tessellation: 8 }, C.gold, { pos: { x, y: 0.8, z } }));
    g.add(cylinder(scene, "lampShade", { height: 0.32, diameterTop: 0.26, diameterBottom: 0.4, tessellation: 12 }, "#FFE3A8", { pos: { x, y: 1.72, z } }));
  }
}

function chipsSign(b: PartBatch): void {
  const s = CASINO_CHIPS_SIGN;
  const { x, z } = s.position;
  // Lectern base (encloses its collider) and the board's backing.
  b.boxBase(0.56, 0.95, 0.36, C.wood, x, 0, z);
  b.box(0.06, 0.25, 0.06, C.gold, x, 1.0, z - 0.1);
  b.box(1.35, 0.88, 0.04, C.wood, x, 1.5, z - 0.19);
}
